// src/components/PeriodBar.jsx
// 授業を選ぶ欄は「今の期間」の授業だけ出す【2026-09-17】
//
//   教務が決めた学期の期間（教務管理 → 学期の期間）から、今日の学期を決める。
//   学期と学期の間の休みは、次に始まる学期。通年の授業はいつも一緒に出す。
//   年度・学期をプルダウンで選ばせない。過去の授業を見たいときだけ「ほかの期間」を開く。
//
//   使い方:
//     const period = usePeriod();
//     const shown  = period.filter(classes);       // 今の期間の授業だけ
//     <PeriodBar period={period} />
//   ほかの年度を選ぶと period.year が変わるので、年度で引き直す画面は period.year を依存に入れる。
//
//   選んだ「ほかの期間」は、このタブを閉じるまでどの画面でも同じ（sessionStorage）。
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const KEY = 'classPeriod';

function readSaved() {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (v && Number.isInteger(v.year)) return v;
  } catch (e) { /* 読めなければ今の期間 */ }
  return null;
}

function writeSaved(v) {
  try {
    if (v) sessionStorage.setItem(KEY, JSON.stringify(v));
    else sessionStorage.removeItem(KEY);
  } catch (e) { /* 保存できなくても動く */ }
  try { window.dispatchEvent(new Event('class-period-change')); } catch (e) { /* 古いブラウザ */ }
}

// 同じ画面の中で何度も引かないように、1回分を覚えておく
let termsCache = null;
let termsPromise = null;
function loadTerms(force) {
  if (termsCache && !force) return Promise.resolve(termsCache);
  if (termsPromise && !force) return termsPromise;
  termsPromise = fetch('/api/terms', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } })
    .then(r => r.json())
    .then(d => { termsPromise = null; if (d && d.success) { termsCache = d; return d; } return null; })
    .catch(() => { termsPromise = null; return null; });
  return termsPromise;
}

/** 学期の期間を変えたあとに呼ぶ（教務管理の画面から） */
export function refreshPeriod() {
  termsCache = null;
  return loadTerms(true).then(d => { writeSaved(readSaved()); return d; });
}

/**
 * @param {object} [opts]
 * @param {boolean} [opts.lockYear] 年度は今の年度に固定する（出席を取る画面など）
 */
export function usePeriod(opts = {}) {
  const { lockYear = false } = opts;
  const [info, setInfo] = useState(termsCache);
  const [loaded, setLoaded] = useState(!!termsCache);
  const [saved, setSaved] = useState(readSaved);

  useEffect(() => {
    let alive = true;
    loadTerms().then(d => { if (alive) { setInfo(d); setLoaded(true); } });
    const onChange = () => { setSaved(readSaved()); if (termsCache) setInfo(termsCache); };
    window.addEventListener('class-period-change', onChange);
    return () => { alive = false; window.removeEventListener('class-period-change', onChange); };
  }, []);

  const current = info && info.current;
  const terms = useMemo(() => (info && info.terms) || [], [info]);
  const fullYearIds = useMemo(() => terms.filter(t => t.isFullYear).map(t => t.id), [terms]);

  let sel = saved;
  if (sel && lockYear && current && sel.year !== current.academicYear) sel = null;
  const isCurrent = !sel;
  const year = sel ? sel.year : (current ? current.academicYear : null);
  const termId = sel ? sel.termId : (current ? current.termId : null);
  const term = terms.find(t => t.id === termId) || null;

  // 出す学期（null = 学期で絞らない）
  const termIds = useMemo(() => {
    if (!current) return null;
    if (termId === 'all' || termId == null) return null;
    return Array.from(new Set([termId, ...fullYearIds]));
  }, [current, termId, fullYearIds]);

  const matches = useCallback((c) => {
    if (!current || !c) return true;           // 学期が引けないときは絞らない
    const y = c.academic_year == null || c.academic_year === '' ? null : Number(c.academic_year);
    if (y == null) return year === current.academicYear;   // 年度が未設定の古い授業は今の年度に出す
    if (y !== year) return false;
    if (!termIds) return true;
    if (c.term_id == null || c.term_id === '') return true;  // 学期が未設定の授業は、その年度のどの学期にも出す
    return termIds.includes(Number(c.term_id));
  }, [current, year, termIds]);

  const filter = useCallback((list) => (list || []).filter(matches), [matches]);

  // 選んでいる期間の実際の日付（'YYYY-MM-DD'）【2026-09-28】
  //   学期を選んでいればその学期、すべての学期なら年度全体（4/1〜翌3/31）。
  const dates = useMemo(() => {
    if (year == null) return null;
    if (term) {
      const r = datesFor(term, year, (info && info.termYears) || []);
      if (r) return r;
    }
    return { from: `${year}-04-01`, to: `${year + 1}-03-31` };
  }, [year, term, info]);

  // 出席記録（first_record_date〜last_record_date）が選んでいる期間にかかっているか【2026-09-28】
  //   学期や年度の設定がずれた授業でも、実際に出席を取った期間の一覧には出すため。
  const hasRecordsIn = useCallback((c) => {
    if (!dates || !c || !c.first_record_date || !c.last_record_date) return false;
    return c.first_record_date <= dates.to && c.last_record_date >= dates.from;
  }, [dates]);

  const label = year == null ? ''
    : `${year}年度${termId === 'all' ? '（すべての学期）' : term ? term.name : ''}`;
  // 年度ごとの上書きがあればその日付で見せる【2026-09-18】
  const range = term && !term.isFullYear
    ? rangeFor(term, year, (info && info.termYears) || []) : '';

  // 「すべての授業」の集計・書き出しに付けるクエリ（?academicYear=&termIds=）
  const query = useMemo(() => {
    if (!current || year == null) return '';
    const q = new URLSearchParams({ academicYear: String(year) });
    if (termIds) q.append('termIds', termIds.join(','));
    return q.toString();
  }, [current, year, termIds]);

  return {
    ready: loaded,
    query,
    available: !!current,
    isCurrent, year, termId, term, terms, label, range,
    // 今の期間の実際の日付（学期の変わり目の判定に使う）【2026-09-18】
    startDate: (current && current.startDate) || null,
    endDate:   (current && current.endDate)   || null,
    hasFullYear: fullYearIds.length > 0,
    current, lockYear,
    yearOptions: (info && info.yearOptions) || [],
    canEdit: !!(info && info.canEdit),
    matches, filter, dates, hasRecordsIn,
    select: (y, t) => {
      const isNow = current && y === current.academicYear && t === current.termId;
      const v = isNow ? null : { year: y, termId: t };
      writeSaved(v); setSaved(v);
    },
    reset: () => { writeSaved(null); setSaved(null); },
  };
}

/**
 * その年度のその学期の期間を「4/1〜9/20」の形にする【2026-09-18】
 *   年度ごとの上書き（termYears）があればその日付、無ければ毎年の月日。
 */
function rangeFor(t, year, termYears) {
  const md = (s) => { const p = String(s).split('-'); return `${Number(p[1])}/${Number(p[2])}`; };
  const ov = (termYears || []).find(
    o => Number(o.termId) === Number(t.id) && Number(o.academicYear) === Number(year)
  );
  if (ov) {
    return ov.startDate.slice(0, 4) === ov.endDate.slice(0, 4)
      ? `${md(ov.startDate)}〜${md(ov.endDate)}`
      : `${md(ov.startDate)}〜翌${md(ov.endDate)}`;
  }
  const cross = Number(t.endMonth) < Number(t.startMonth);
  return `${t.startMonth}/${t.startDay}〜${cross ? '翌' : ''}${t.endMonth}/${t.endDay}`;
}

/**
 * その年度のその学期の期間を { from, to }（'YYYY-MM-DD'）で返す【2026-09-28】
 *   サーバーの termRangeForYear と同じ決め方（4月より前の月は翌年）。
 */
function datesFor(t, year, termYears) {
  const pad = (n) => String(n).padStart(2, '0');
  const ov = (termYears || []).find(
    o => Number(o.termId) === Number(t.id) && Number(o.academicYear) === Number(year)
  );
  if (ov) return { from: ov.startDate, to: ov.endDate };
  if (t.startMonth == null || t.endMonth == null) return null;
  const sm = Number(t.startMonth), em = Number(t.endMonth);
  const sy = sm >= 4 ? year : year + 1;
  const ey = em >= 4 ? year : year + 1;
  const last = (y, m) => new Date(y, m, 0).getDate();
  const sd = Math.min(t.startDay == null ? 1 : Number(t.startDay), last(sy, sm));
  const ed = Math.min(t.endDay == null ? 31 : Number(t.endDay), last(ey, em));
  return { from: `${sy}-${pad(sm)}-${pad(sd)}`, to: `${ey}-${pad(em)}-${pad(ed)}` };
}

const chip = (on) => ({
  padding: '0.2rem 0.6rem', borderRadius: 999, fontSize: '0.8rem', cursor: 'pointer',
  border: `1px solid ${on ? '#2563eb' : '#d1d5db'}`,
  background: on ? '#2563eb' : '#fff', color: on ? '#fff' : '#374151',
});

/**
 * 今の期間の表示と「ほかの期間」
 * @param {object} props
 * @param {object} props.period usePeriod() の戻り値
 * @param {number} [props.hiddenCount] 期間の外にあって出していない授業の数
 * @param {object} [props.style]
 */
export default function PeriodBar({ period, hiddenCount, style }) {
  const [open, setOpen] = useState(false);
  if (!period || !period.ready || !period.available) return null;
  const p = period;
  const cur = p.current;
  const years = p.lockYear
    ? [cur.academicYear]
    : Array.from(new Set([...p.yearOptions, cur.academicYear, p.year])).filter(Boolean).sort((a, b) => b - a);
  const partTerms = p.terms.filter(t => !t.isFullYear);
  const fullTerms = p.terms.filter(t => t.isFullYear);

  return (
    <div data-period-bar style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.5rem', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
                    padding: '0.35rem 0.6rem', borderRadius: 8,
                    background: p.isCurrent ? '#eff6ff' : '#fffbeb',
                    border: `1px solid ${p.isCurrent ? '#bfdbfe' : '#fcd34d'}` }}>
        <span style={{ fontWeight: 700, color: p.isCurrent ? '#1d4ed8' : '#92400e' }}>
          {p.isCurrent ? '今の期間' : 'ほかの期間を表示中'}
        </span>
        <span data-period-label>
          {p.label}
          {p.range && <span style={{ color: '#6b7280' }}>（{p.range}）</span>}
          {p.termId !== 'all' && p.hasFullYear && p.term && !p.term.isFullYear && <span style={{ color: '#6b7280' }}>＋通年</span>}
          の授業
        </span>
        {hiddenCount > 0 && <span style={{ color: '#6b7280' }}>（ほかの期間に {hiddenCount}件）</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          {!p.isCurrent && (
            <button type="button" className="btn-secondary" style={{ padding: '0.15rem 0.5rem', fontSize: '0.8rem' }}
                    onClick={() => { p.reset(); setOpen(false); }}>
              今の期間に戻る
            </button>
          )}
          <button type="button" className="btn-link" style={{ fontSize: '0.8rem' }}
                  aria-expanded={open} onClick={() => setOpen(o => !o)}>
            {open ? '閉じる' : 'ほかの期間'}
          </button>
        </span>
      </div>

      {open && (
        <div data-period-panel style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.6rem', marginTop: '0.3rem', background: '#fff' }}>
          {years.map(y => (
            <div key={y} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', margin: '0.2rem 0' }}>
              <span style={{ minWidth: '5.5em', fontWeight: 600 }}>{y}年度</span>
              {partTerms.map(t => (
                <button type="button" key={t.id} style={chip(p.year === y && p.termId === t.id)}
                        onClick={() => { p.select(y, t.id); setOpen(false); }}>
                  {t.name}{y === cur.academicYear && t.id === cur.termId ? '（今）' : ''}
                </button>
              ))}
              {fullTerms.map(t => (
                <button type="button" key={t.id} style={chip(p.year === y && p.termId === t.id)}
                        onClick={() => { p.select(y, t.id); setOpen(false); }}>
                  {t.name}だけ
                </button>
              ))}
              <button type="button" style={chip(p.year === y && p.termId === 'all')}
                      onClick={() => { p.select(y, 'all'); setOpen(false); }}>
                すべての学期
              </button>
            </div>
          ))}
          <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: '0.3rem' }}>
            今の期間は、教務が決めた学期の期間から自動で決まります。
            {p.canEdit && <> <Link to="/academic?tab=terms">学期の期間を変える</Link></>}
          </div>
        </div>
      )}
    </div>
  );
}
